import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Collapse,
  Divider,
  Popover,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useLayoutPreferences } from '../../../context/LayoutPreferencesContext';

// ---- Types & Constants ----
type Channel = 'MAILING' | 'EMAIL' | 'SMS' | 'TIKEO' | 'COUPON_CAISSE';

interface Operation {
  id: string;
  name: string;
  channel: Channel;
  status: string;
  type: string;
  startDate: string;
  endDate: string;
}

const CHANNELS: Array<{
  value: Channel;
  label: string;
  Icon: React.ElementType;
  color: string;
}> = [
  { value: 'MAILING',      label: 'Mailing',       Icon: MailOutlineIcon,  color: '#f59e0b' },
  { value: 'EMAIL',        label: 'Emailing',       Icon: EmailIcon,        color: '#3b82f6' },
  { value: 'SMS',          label: 'SMS',            Icon: SmsIcon,          color: '#22c55e' },
  { value: 'TIKEO',        label: 'Tikeo',          Icon: LocalActivityIcon, color: '#a855f7' },
  { value: 'COUPON_CAISSE',label: 'Coupon Caisse',  Icon: PointOfSaleIcon,  color: '#f43f5e' },
];

const STATUS_OPTIONS  = ['Toutes les opérations', 'Envoyée', 'Planifiée', 'Brouillon'];
const TYPE_OPTIONS    = ['Opérations non récurrentes', 'Opérations récurrentes', 'Toutes'];
const STORE_OPTIONS   = ['Tous les magasins', 'Paris', 'Lyon', 'Marseille'];

const MOCK_OPERATIONS: Operation[] = [
  { id: '1',  name: 'SMS_FEVRIER_PROMO',           channel: 'SMS',          status: 'Envoyée',  type: 'Standard', startDate: '2026-03-01', endDate: '2026-03-14' },
  { id: '2',  name: 'EMAIL_ELECTRICITE_MARS26',    channel: 'EMAIL',        status: 'Envoyée',  type: 'Standard', startDate: '2026-03-02', endDate: '2026-03-10' },
  { id: '3',  name: 'EMAIL_MENUISERIE_MARS26',     channel: 'EMAIL',        status: 'Planifiée',type: 'Standard', startDate: '2026-03-05', endDate: '2026-03-17' },
  { id: '4',  name: 'EMAIL_PLOMBERIE_MARS26',      channel: 'EMAIL',        status: 'Envoyée',  type: 'Standard', startDate: '2026-03-03', endDate: '2026-03-11' },
  { id: '5',  name: 'EMAIL_PEINTRE_MARS26',        channel: 'EMAIL',        status: 'Envoyée',  type: 'Standard', startDate: '2026-03-04', endDate: '2026-03-10' },
  { id: '6',  name: 'EMAIL_MACONNERIE_MARS26',     channel: 'EMAIL',        status: 'Envoyée',  type: 'Standard', startDate: '2026-03-04', endDate: '2026-03-12' },
  { id: '7',  name: 'SMS_OUVERTURE_PARIS',         channel: 'SMS',          status: 'Planifiée',type: 'Standard', startDate: '2026-03-06', endDate: '2026-03-20' },
  { id: '8',  name: 'SMS_OUVERTURE_LYON',          channel: 'SMS',          status: 'Planifiée',type: 'Standard', startDate: '2026-03-08', endDate: '2026-03-22' },
  { id: '9',  name: 'MAILING_PRINTEMPS_2026',      channel: 'MAILING',      status: 'Brouillon',type: 'Standard', startDate: '2026-03-15', endDate: '2026-04-15' },
  { id: '10', name: 'EMAIL_CARLAGE_MARS26',        channel: 'EMAIL',        status: 'Envoyée',  type: 'Standard', startDate: '2026-03-04', endDate: '2026-03-11' },
  { id: '11', name: 'TIKEO_PROMO_MARS',            channel: 'TIKEO',        status: 'Planifiée',type: 'Standard', startDate: '2026-03-10', endDate: '2026-04-10' },
  { id: '12', name: 'EMAIL_PLATRIER_MARS26',       channel: 'EMAIL',        status: 'Envoyée',  type: 'Standard', startDate: '2026-03-04', endDate: '2026-03-11' },
  { id: '13', name: 'SMS_MARS_2MARS',              channel: 'SMS',          status: 'Envoyée',  type: 'Standard', startDate: '2026-03-02', endDate: '2026-03-07' },
  { id: '14', name: 'COUPON_REMISE_PRINTEMPS',     channel: 'COUPON_CAISSE',status: 'Planifiée',type: 'Standard', startDate: '2026-03-20', endDate: '2026-04-20' },
  { id: '15', name: 'EMAIL_SOLDES_AVRIL',          channel: 'EMAIL',        status: 'Brouillon',type: 'Standard', startDate: '2026-04-01', endDate: '2026-04-30' },
];

const DAY_WIDTH      = 30; // px
const ROW_HEIGHT     = 38; // px
const LEFT_COL_W     = 230; // px
const MONTH_HDR_H    = 44; // px
const DAY_HDR_H      = 26; // px

const MONTH_NAMES_FR = [
  'Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre',
];

// ---- Helpers ----
const daysInMonth = (year: number, month: number) =>
  new Date(year, month + 1, 0).getDate();

const parseDate = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const diffDays = (a: Date, b: Date) =>
  Math.round((a.getTime() - b.getTime()) / 86400000);

// ---- Component ----
const MarketingCalendar: React.FC = () => {
  const { sidebarColor } = useLayoutPreferences();

  const today = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const [filterOpen,     setFilterOpen]     = useState(true);
  const [activeChannels, setActiveChannels] = useState<Set<Channel>>(
    new Set(CHANNELS.map(c => c.value)),
  );
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
  const [typeFilter,   setTypeFilter]   = useState(TYPE_OPTIONS[0]);
  const [storeFilter,  setStoreFilter]  = useState(STORE_OPTIONS[0]);
  const [legendAnchor, setLegendAnchor] = useState<HTMLElement | null>(null);

  // Two-month window
  const m2Year  = viewMonth === 11 ? viewYear + 1 : viewYear;
  const m2Month = viewMonth === 11 ? 0 : viewMonth + 1;
  const m1Days  = daysInMonth(viewYear, viewMonth);
  const m2Days  = daysInMonth(m2Year, m2Month);
  const totalDays  = m1Days + m2Days;
  const totalWidth = totalDays * DAY_WIDTH;
  const viewStart  = new Date(viewYear, viewMonth, 1);
  const viewEnd    = new Date(m2Year, m2Month, m2Days);

  const todayOffset = diffDays(today, viewStart);

  const prev = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const next = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const toggleChannel = (ch: Channel) =>
    setActiveChannels(prev => {
      const next = new Set(prev);
      next.has(ch) ? next.delete(ch) : next.add(ch);
      return next;
    });

  const filteredOps = useMemo(() => MOCK_OPERATIONS.filter(op => {
    if (!activeChannels.has(op.channel)) return false;
    if (statusFilter !== STATUS_OPTIONS[0] && op.status !== statusFilter) return false;
    if (typeFilter   !== TYPE_OPTIONS[0]   && op.type   !== typeFilter)   return false;
    const opStart = parseDate(op.startDate);
    const opEnd   = parseDate(op.endDate);
    return opEnd >= viewStart && opStart <= viewEnd;
  }), [activeChannels, statusFilter, typeFilter, viewStart, viewEnd]);

  const getCh = (ch: Channel) => CHANNELS.find(c => c.value === ch)!;

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography
        variant="h5" fontWeight={700}
        sx={{ mb: 2, letterSpacing: 1, textTransform: 'uppercase', color: '#495057' }}
      >
        Planning des opérations
      </Typography>

      {/* ── Filter Card ── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
               px: 2.5, py: 1.5, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setFilterOpen(o => !o)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ color: sidebarColor, fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={600}>Filtrer l'affichage</Typography>
          </Box>
          {filterOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </Box>

        <Collapse in={filterOpen}>
          <Divider />
          <CardContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>

              {/* Canal */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}
                  sx={{ display: 'block', mb: 1 }}>
                  Canal
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {CHANNELS.map(({ value, label, color, Icon }) => {
                    const on = activeChannels.has(value);
                    return (
                      <Chip
                        key={value}
                        icon={<Icon style={{ fontSize: 14 }} />}
                        label={label}
                        size="small"
                        onClick={() => toggleChannel(value)}
                        sx={{
                          border: '1px solid',
                          borderColor: on ? color : '#e2e8f0',
                          backgroundColor: on ? `${color}18` : 'transparent',
                          color: on ? color : '#94a3b8',
                          fontWeight: on ? 600 : 400,
                          '& .MuiChip-icon': { color: on ? color : '#cbd5e1' },
                          transition: 'all 0.15s',
                          cursor: 'pointer',
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Statut */}
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}
                  sx={{ display: 'block', mb: 1 }}>Statut</Typography>
                <FormControl size="small" fullWidth>
                  <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>

              {/* Type */}
              <Box sx={{ minWidth: 220 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}
                  sx={{ display: 'block', mb: 1 }}>Type</Typography>
                <FormControl size="small" fullWidth>
                  <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                    {TYPE_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>

              {/* Magasin */}
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}
                  sx={{ display: 'block', mb: 1 }}>Magasin</Typography>
                <FormControl size="small" fullWidth>
                  <Select value={storeFilter} onChange={e => setStoreFilter(e.target.value)}>
                    {STORE_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained" size="small"
                startIcon={<FilterListIcon />}
                sx={{
                  backgroundColor: sidebarColor, height: 36,
                  '&:hover': { backgroundColor: sidebarColor, filter: 'brightness(0.9)' },
                  textTransform: 'none', fontWeight: 600,
                }}
              >
                Filtrer l'affichage
              </Button>
            </Box>
          </CardContent>
        </Collapse>
      </Card>

      {/* ── Timeline Card ── */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        {/* Nav + legend */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.25, borderBottom: '1px solid #e2e8f0',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={prev} sx={{ color: sidebarColor }}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={700}
              sx={{ minWidth: 210, textAlign: 'center', fontSize: '0.95rem' }}>
              {MONTH_NAMES_FR[viewMonth]} / {MONTH_NAMES_FR[m2Month]}{' '}
              {m2Year !== viewYear ? `${viewYear} – ${m2Year}` : viewYear}
            </Typography>
            <IconButton size="small" onClick={next} sx={{ color: sidebarColor }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <Button
            size="small" variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={e => setLegendAnchor(e.currentTarget)}
            sx={{
              color: sidebarColor, borderColor: sidebarColor,
              textTransform: 'none', fontWeight: 600,
              '&:hover': { borderColor: sidebarColor, bgcolor: `${sidebarColor}10` },
            }}
          >
            Afficher la légende
          </Button>
        </Box>

        {/* Legend popover */}
        <Popover
          open={Boolean(legendAnchor)}
          anchorEl={legendAnchor}
          onClose={() => setLegendAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Paper sx={{ p: 2, minWidth: 210 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Canaux
            </Typography>
            {CHANNELS.map(({ value, label, color, Icon }) => (
              <Box key={value} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 28, height: 10, borderRadius: 1, bgcolor: color, flexShrink: 0 }} />
                <Icon style={{ fontSize: 14, color }} />
                <Typography variant="body2">{label}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600}
              sx={{ display: 'block', mb: 1 }}>Statut</Typography>
            {[
              { label: 'Envoyée',   color: '#22c55e' },
              { label: 'Planifiée', color: '#3b82f6' },
              { label: 'Brouillon', color: '#94a3b8' },
            ].map(({ label, color }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%',
                           border: `2.5px solid ${color}`, flexShrink: 0 }} />
                <Typography variant="body2">{label}</Typography>
              </Box>
            ))}
          </Paper>
        </Popover>

        {/* ── Gantt ── */}
        <Box sx={{ overflowX: 'auto' }}>

          {/* Month header row */}
          <Box sx={{
            display: 'flex', position: 'sticky', top: 0, zIndex: 20,
            bgcolor: '#f8f9fa', borderBottom: '1px solid #e2e8f0',
          }}>
            <Box sx={{
              width: LEFT_COL_W, flexShrink: 0, height: MONTH_HDR_H,
              display: 'flex', alignItems: 'center', px: 2,
              position: 'sticky', left: 0, zIndex: 25,
              bgcolor: '#f8f9fa', borderRight: '1px solid #e2e8f0',
            }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Opération
              </Typography>
            </Box>
            {[
              { year: viewYear, month: viewMonth, days: m1Days },
              { year: m2Year,   month: m2Month,   days: m2Days },
            ].map((m, i) => (
              <Box key={i} sx={{
                width: m.days * DAY_WIDTH, flexShrink: 0,
                height: MONTH_HDR_H,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRight: i === 0
                  ? `2px solid ${sidebarColor}30`
                  : 'none',
              }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#374151' }}>
                  {MONTH_NAMES_FR[m.month]} {m.year}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Day-number header row */}
          <Box sx={{
            display: 'flex', position: 'sticky', top: MONTH_HDR_H, zIndex: 20,
            bgcolor: '#f8f9fa', borderBottom: '2px solid #e2e8f0',
          }}>
            <Box sx={{
              width: LEFT_COL_W, flexShrink: 0, height: DAY_HDR_H,
              position: 'sticky', left: 0, zIndex: 25,
              bgcolor: '#f8f9fa', borderRight: '1px solid #e2e8f0',
            }} />
            {[
              { days: m1Days, monthIdx: 0 },
              { days: m2Days, monthIdx: 1 },
            ].map(({ days, monthIdx }) =>
              Array.from({ length: days }, (_, d) => {
                const absDay  = (monthIdx === 0 ? 0 : m1Days) + d;
                const isToday = absDay === todayOffset;
                return (
                  <Box key={`${monthIdx}-${d}`} sx={{
                    width: DAY_WIDTH, flexShrink: 0, height: DAY_HDR_H,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: isToday ? `${sidebarColor}22` : 'transparent',
                    borderRight: d === days - 1 && monthIdx === 0
                      ? `2px solid ${sidebarColor}30`
                      : '1px solid #f1f5f9',
                  }}>
                    <Typography sx={{
                      fontSize: '0.62rem',
                      fontWeight: isToday ? 800 : 500,
                      color: isToday ? sidebarColor : '#94a3b8',
                    }}>
                      {d + 1}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>

          {/* Operation rows */}
          {filteredOps.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Aucune opération ne correspond aux filtres sélectionnés.
              </Typography>
            </Box>
          ) : (
            filteredOps.map((op, rowIdx) => {
              const conf     = getCh(op.channel);
              const opStart  = parseDate(op.startDate);
              const opEnd    = parseDate(op.endDate);
              const startDay = Math.max(0, diffDays(opStart, viewStart));
              const endDay   = Math.min(totalDays, diffDays(opEnd, viewStart) + 1);
              const leftPx   = startDay * DAY_WIDTH;
              const widthPx  = Math.max(DAY_WIDTH, (endDay - startDay) * DAY_WIDTH);
              const isEven   = rowIdx % 2 === 0;
              const isDraft  = op.status === 'Brouillon';

              return (
                <Box
                  key={op.id}
                  sx={{
                    display: 'flex',
                    borderBottom: '1px solid #f1f5f9',
                    '&:hover .gantt-name': { color: sidebarColor },
                    '&:hover': { bgcolor: `${sidebarColor}06` },
                  }}
                >
                  {/* Left: operation name (sticky) */}
                  <Box sx={{
                    width: LEFT_COL_W, flexShrink: 0, height: ROW_HEIGHT,
                    display: 'flex', alignItems: 'center', gap: 1, px: 1.5,
                    position: 'sticky', left: 0, zIndex: 10,
                    bgcolor: isEven ? '#fafbfc' : '#fff',
                    borderRight: '1px solid #e2e8f0',
                  }}>
                    <conf.Icon sx={{ fontSize: 13, color: conf.color, flexShrink: 0 }} />
                    <Tooltip title={op.name} placement="right">
                      <Typography
                        className="gantt-name"
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap', color: '#374151',
                          fontSize: '0.71rem', transition: 'color 0.15s',
                        }}
                      >
                        {op.name}
                      </Typography>
                    </Tooltip>
                  </Box>

                  {/* Right: bar area */}
                  <Box sx={{
                    position: 'relative', width: totalWidth, height: ROW_HEIGHT,
                    bgcolor: isEven ? '#fafbfc' : '#fff', flexShrink: 0,
                  }}>
                    {/* Today line */}
                    {todayOffset >= 0 && todayOffset <= totalDays && (
                      <Box sx={{
                        position: 'absolute', left: todayOffset * DAY_WIDTH,
                        top: 0, bottom: 0, width: 2,
                        bgcolor: `${sidebarColor}40`, zIndex: 1,
                        pointerEvents: 'none',
                      }} />
                    )}
                    {/* Month separator */}
                    <Box sx={{
                      position: 'absolute', left: m1Days * DAY_WIDTH,
                      top: 0, bottom: 0, width: 2,
                      bgcolor: `${sidebarColor}15`, pointerEvents: 'none',
                    }} />

                    {/* Operation bar */}
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="caption" fontWeight={700} display="block">
                            {op.name}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Du {op.startDate} au {op.endDate}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Statut : {op.status}
                          </Typography>
                        </Box>
                      }
                      arrow
                    >
                      <Box sx={{
                        position: 'absolute',
                        left: leftPx, top: 5,
                        height: ROW_HEIGHT - 10, width: widthPx,
                        bgcolor: conf.color,
                        borderRadius: 1.5,
                        display: 'flex', alignItems: 'center', gap: 0.75, px: 1,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        zIndex: 2,
                        opacity: isDraft ? 0.5 : 1,
                        boxShadow: `0 1px 4px ${conf.color}55`,
                        transition: 'transform 0.1s, box-shadow 0.1s',
                        '&:hover': {
                          transform: 'scaleY(1.08)',
                          boxShadow: `0 2px 8px ${conf.color}80`,
                        },
                        ...(isDraft && {
                          background: `repeating-linear-gradient(
                            45deg,
                            ${conf.color},
                            ${conf.color} 6px,
                            ${conf.color}bb 6px,
                            ${conf.color}bb 12px
                          )`,
                        }),
                      }}>
                        <conf.Icon sx={{ fontSize: 12, color: '#fff', flexShrink: 0 }} />
                        {widthPx > 56 && (
                          <Typography sx={{
                            color: '#fff', fontSize: '0.61rem', fontWeight: 600,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap', letterSpacing: 0.3,
                          }}>
                            {op.name}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default MarketingCalendar;
